import { EmailError } from '../map-error.js'

const COPY = Object.freeze({
  'zh-CN': {
    subject: title => `您的 VOKO 视频已生成：${title}`,
    heading: '您的视频已生成',
    intro: '视频和多语言文案均已处理完成。',
    title: '视频标题',
    summary: '视频简介',
    watch: '观看视频',
    footer: '感谢您使用 VOKO。',
  },
  en: {
    subject: title => `Your VOKO video is ready: ${title}`,
    heading: 'Your video is ready',
    intro: 'Your video and localized details are ready.',
    title: 'Video title',
    summary: 'Video description',
    watch: 'Watch your video',
    footer: 'Thank you for using VOKO.',
  },
  ja: {
    subject: title => `VOKOの動画が完成しました：${title}`,
    heading: '動画が完成しました',
    intro: '動画と多言語コンテンツの準備が完了しました。',
    title: '動画タイトル',
    summary: '動画の紹介',
    watch: '動画を見る',
    footer: 'VOKOをご利用いただきありがとうございます。',
  },
  ko: {
    subject: title => `VOKO 동영상이 준비되었습니다: ${title}`,
    heading: '동영상이 준비되었습니다',
    intro: '동영상과 다국어 콘텐츠 처리가 모두 완료되었습니다.',
    title: '동영상 제목',
    summary: '동영상 소개',
    watch: '동영상 보기',
    footer: 'VOKO를 이용해 주셔서 감사합니다.',
  },
  es: {
    subject: title => `Tu video de VOKO está listo: ${title}`,
    heading: 'Tu video está listo',
    intro: 'Tu video y sus textos localizados ya están listos.',
    title: 'Título del video',
    summary: 'Descripción del video',
    watch: 'Ver tu video',
    footer: 'Gracias por usar VOKO.',
  },
  pt: {
    subject: title => `Seu vídeo do VOKO está pronto: ${title}`,
    heading: 'Seu vídeo está pronto',
    intro: 'Seu vídeo e os textos localizados estão prontos.',
    title: 'Título do vídeo',
    summary: 'Descrição do vídeo',
    watch: 'Assistir ao vídeo',
    footer: 'Obrigado por usar o VOKO.',
  },
})

/**
 * 视频成片邮件（Scenario 既有路径）。`kind: 'story_episode'` 时转交 Story 短剧集文案。
 * @param {{ locale: string, videoTitle: string, videoSummary: string, videoUrl: string, kind?: string, isFree?: boolean, episodeTitle?: string, episodeSummary?: string, episodeUrl?: string }} data
 */
export function render(data) {
  if (!data || typeof data !== 'object') fail('data is required')
  if (data.kind === 'story_episode') {
    return renderStoryEpisode({
      locale: data.locale,
      episodeTitle: data.episodeTitle,
      episodeSummary: data.episodeSummary,
      episodeUrl: data.episodeUrl,
      isFree: data.isFree,
    })
  }
  if (!data || typeof data !== 'object') fail('data is required')
  const copy = COPY[data.locale]
  if (!copy) fail(`unsupported locale: ${String(data.locale)}`)
  const videoTitle = requiredText(data.videoTitle, 'videoTitle')
  const videoSummary = requiredText(data.videoSummary, 'videoSummary')
  const videoUrl = requiredUrl(data.videoUrl)
  const subject = copy.subject(videoTitle)
  const text = [
    copy.heading,
    '',
    copy.intro,
    '',
    `${copy.title}: ${videoTitle}`,
    `${copy.summary}: ${videoSummary}`,
    `${copy.watch}: ${videoUrl}`,
    '',
    copy.footer,
  ].join('\n')
  const html = [
    '<!doctype html><html><body style="font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;line-height:1.6;">',
    `<h2 style="margin:0 0 16px;">${escapeHtml(copy.heading)}</h2>`,
    `<p style="margin:0 0 16px;">${escapeHtml(copy.intro)}</p>`,
    `<p style="margin:0 0 8px;"><strong>${escapeHtml(copy.title)}:</strong> ${escapeHtml(videoTitle)}</p>`,
    `<p style="margin:0 0 16px;"><strong>${escapeHtml(copy.summary)}:</strong> ${escapeHtml(videoSummary)}</p>`,
    `<p style="margin:0 0 16px;"><a href="${escapeHtml(videoUrl)}">${escapeHtml(copy.watch)}</a></p>`,
    `<p style="margin:0;color:#666;">${escapeHtml(copy.footer)}</p>`,
    '</body></html>',
  ].join('')
  return { subject, html, text }
}

// Story 短剧集完成邮件：同一模板家族，文案按「集」而非「视频」，可带首免说明。
const STORY_COPY = Object.freeze({
  'zh-CN': {
    subject: title => `你的短剧新集已完成：${title}`,
    heading: '你的短剧新集已完成',
    intro: '本集已生成完毕，可直接观看。',
    title: '本集标题',
    summary: '本集简介',
    watch: '观看本集',
    freeNote: '这是你的首次免费短剧，不消耗积分。',
    footer: '感谢您使用 VOKO。',
  },
  en: {
    subject: title => `Your new episode is ready: ${title}`,
    heading: 'Your new episode is ready',
    intro: 'This episode has finished generating and is ready to watch.',
    title: 'Episode title',
    summary: 'Episode summary',
    watch: 'Watch episode',
    freeNote: 'This is your first free episode and costs no credits.',
    footer: 'Thank you for using VOKO.',
  },
  ja: {
    subject: title => `新しいエピソードが完成しました：${title}`,
    heading: '新しいエピソードが完成しました',
    intro: 'このエピソードの生成が完了し、すぐに視聴できます。',
    title: 'エピソードタイトル',
    summary: 'エピソードの紹介',
    watch: 'エピソードを見る',
    freeNote: 'これは初回無料エピソードのため、クレジットは消費されません。',
    footer: 'VOKOをご利用いただきありがとうございます。',
  },
  ko: {
    subject: title => `새 에피소드가 준비되었습니다: ${title}`,
    heading: '새 에피소드가 준비되었습니다',
    intro: '이 에피소드 생성이 완료되어 바로 시청할 수 있습니다.',
    title: '에피소드 제목',
    summary: '에피소드 소개',
    watch: '에피소드 보기',
    freeNote: '첫 무료 에피소드이므로 크레딧이 차감되지 않습니다.',
    footer: 'VOKO를 이용해 주셔서 감사합니다.',
  },
  es: {
    subject: title => `Tu nuevo episodio está listo: ${title}`,
    heading: 'Tu nuevo episodio está listo',
    intro: 'Este episodio terminó de generarse y ya puedes verlo.',
    title: 'Título del episodio',
    summary: 'Resumen del episodio',
    watch: 'Ver episodio',
    freeNote: 'Este es tu primer episodio gratis y no consume créditos.',
    footer: 'Gracias por usar VOKO.',
  },
  pt: {
    subject: title => `Seu novo episódio está pronto: ${title}`,
    heading: 'Seu novo episódio está pronto',
    intro: 'Este episódio terminou de ser gerado e já pode ser assistido.',
    title: 'Título do episódio',
    summary: 'Resumo do episódio',
    watch: 'Assistir episódio',
    freeNote: 'Este é o seu primeiro episódio grátis e não consome créditos.',
    footer: 'Obrigado por usar o VOKO.',
  },
})

/**
 * Story 短剧集完成邮件：标题/摘要必须是真实非空实值；`isFree` 为真时追加首免说明。
 * 语言回退由调用方按「原语言实值」处理，本函数不补造任何文案。
 * @param {{ locale: string, episodeTitle: string, episodeSummary: string, episodeUrl: string, isFree?: boolean }} data
 */
export function renderStoryEpisode(data) {
  if (!data || typeof data !== 'object') fail('story episode data is required')
  const copy = STORY_COPY[data.locale]
  if (!copy) fail(`unsupported locale: ${String(data.locale)}`)
  const episodeTitle = requiredText(data.episodeTitle, 'episodeTitle')
  const episodeSummary = requiredText(data.episodeSummary, 'episodeSummary')
  const episodeUrl = requiredUrl(data.episodeUrl)
  if (data.isFree !== undefined && typeof data.isFree !== 'boolean') fail('isFree must be a boolean')
  const subject = copy.subject(episodeTitle)
  const lines = [
    copy.heading,
    '',
    copy.intro,
    '',
    `${copy.title}: ${episodeTitle}`,
    `${copy.summary}: ${episodeSummary}`,
  ]
  if (data.isFree === true) lines.push('', copy.freeNote)
  lines.push(`${copy.watch}: ${episodeUrl}`, '', copy.footer)
  const paragraphs = [
    `<p style="margin:0 0 16px;">${escapeHtml(copy.intro)}</p>`,
    `<p style="margin:0 0 8px;"><strong>${escapeHtml(copy.title)}:</strong> ${escapeHtml(episodeTitle)}</p>`,
    `<p style="margin:0 0 16px;"><strong>${escapeHtml(copy.summary)}:</strong> ${escapeHtml(episodeSummary)}</p>`,
  ]
  if (data.isFree === true) paragraphs.push(`<p style="margin:0 0 16px;">${escapeHtml(copy.freeNote)}</p>`)
  paragraphs.push(
    `<p style="margin:0 0 16px;"><a href="${escapeHtml(episodeUrl)}">${escapeHtml(copy.watch)}</a></p>`,
    `<p style="margin:0;color:#666;">${escapeHtml(copy.footer)}</p>`,
  )
  const html = [
    '<!doctype html><html><body style="font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;line-height:1.6;">',
    `<h2 style="margin:0 0 16px;">${escapeHtml(copy.heading)}</h2>`,
    ...paragraphs,
    '</body></html>',
  ].join('')
  return { subject, html, text: lines.join('\n') }
}

function requiredText(value, field) {
  if (typeof value === 'string' && value.trim().length > 0) return value.trim()
  fail(`missing field "${field}"`)
}

function requiredUrl(value) {
  const url = requiredText(value, 'videoUrl')
  if (new URL(url).protocol === 'https:') return url
  fail('videoUrl must use https')
}

function fail(message) {
  throw new EmailError({ provider: 'template', reason: 'badTemplate', retryable: false, message: `video-ready: ${message}` })
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}
